/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Grid,
  Snackbar,
} from '@material-ui/core';
import { useQuery } from 'react-query';
import { useAuth } from '../../../../auth/authProvider';
import request from '../../../../services/network/request';
import { DiscountType } from '../../../../helpers/enums';

const ruleTypes = [
  { value: DiscountType.DISTRIBUIDORA, label: 'Distribuidora' },
  { value: DiscountType.TIPO_PESSOA, label: 'Tipo de Pessoa' },
  { value: DiscountType.FAIXA_CONSUMO, label: 'Faixa de Consumo (MW)' },
];

const beneficiaryTypes = [
  { value: 'BOTH', label: 'Ambos' },
  { value: 'REFERRER', label: 'Indicador' },
  { value: 'REFERRED', label: 'Indicado' },
];

const personTypes = [
  { value: 'JURIDICAL', label: 'Pessoa jurídica' },
  { value: 'NATURAL', label: 'Pessoa física' },
];

const initialFormState = {
  discountType: '',
  discountValue: '',
  mwMinValue: '',
  mwMaxValue: '',
  description: '',
  discountAmount: '',
  beneficiary: '',
};

const RegraDescontoModal = ({ open, onClose, rule, refetch }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    if (rule) {
      setFormData({
        discountType: rule.discountType || '',
        discountValue: rule.discountValue || '',
        mwMinValue: rule.mwMinValue ? String(rule.mwMinValue) : '',
        mwMaxValue: rule.mwMaxValue ? String(rule.mwMaxValue) : '',
        description: rule.description || '',
        discountAmount: rule.discountAmount ? String(rule.discountAmount) : '',
        beneficiary: rule.beneficiary || '',
      });
    } else {
      setFormData(initialFormState);
    }
    setErrors({});
  }, [rule, open]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.discountType) {
      newErrors.discountType = 'Tipo de regra é obrigatório';
    }

    if (!formData.discountAmount) {
      newErrors.discountAmount = 'Valor do desconto é obrigatório';
    } else if (Number.isNaN(Number(formData.discountAmount)) || Number(formData.discountAmount) <= 0) {
      newErrors.discountAmount = 'Valor do desconto deve ser um número positivo';
    }

    if (!formData.beneficiary) {
      newErrors.beneficiary = 'Beneficiário é obrigatório';
    }

    if (formData.discountType === DiscountType.DISTRIBUIDORA && !formData.discountValue) {
      newErrors.discountValue = 'Nome da distribuidora é obrigatório';
    }

    if (formData.discountType === DiscountType.TIPO_PESSOA && !formData.discountValue) {
      newErrors.discountValue = 'Tipo de pessoa é obrigatório';
    }

    if (formData.discountType === DiscountType.FAIXA_CONSUMO) {
      if (!formData.mwMinValue && !formData.mwMaxValue) {
        newErrors.mwMinValue = 'Pelo menos um dos valores deve ser preenchido';
        newErrors.mwMaxValue = 'Pelo menos um dos valores deve ser preenchido';
      } else {
        if (formData.mwMinValue && (Number.isNaN(Number(formData.mwMinValue)) || Number(formData.mwMinValue) < 0)) {
          newErrors.mwMinValue = 'Valor mínimo deve ser um número não negativo';
        }
        if (formData.mwMaxValue && (Number.isNaN(Number(formData.mwMaxValue)) || Number(formData.mwMaxValue) < 0)) {
          newErrors.mwMaxValue = 'Valor máximo deve ser um número não negativo';
        }
        if (formData.mwMinValue && formData.mwMaxValue && Number(formData.mwMinValue) >= Number(formData.mwMaxValue)) {
          newErrors.mwMaxValue = 'Valor máximo deve ser maior que o valor mínimo';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    const apiData = {
      ...formData,
      mwMinValue: formData.mwMinValue ? Number(formData.mwMinValue) : null,
      mwMaxValue: formData.mwMaxValue ? Number(formData.mwMaxValue) : null,
      discountAmount: Number(formData.discountAmount),
    };

    try {
      if (rule) {
        await request.put(`discount-rules/${rule.id}`, apiData, { token });
      } else {
        await request.post('discount-rules', apiData, { token });
      }

      refetch();
      onClose();
    } catch (error) {
      setSnackbar({ open: true, message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const FIVE_MINUTES = 300000;

  const { data: energyUtilities = [] } = useQuery(
    ['fetch-dx'],
    () => request.get('discount-rules/energy-utilities', { token }),
    {
      enabled: formData.discountType === DiscountType.DISTRIBUIDORA,
      staleTime: FIVE_MINUTES,
      cacheTime: FIVE_MINUTES * 2,
    },
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{rule ? 'Editar Regra de Desconto' : 'Nova Regra de Desconto'}</DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.discountType}>
              <InputLabel id="type-label">Tipo de Regra</InputLabel>
              <Select
                labelId="type-label"
                name="discountType"
                value={formData.discountType}
                onChange={handleChange}
                disabled={!!rule}
              >
                {ruleTypes.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.discountType && <FormHelperText>{errors.discountType}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.beneficiary}>
              <InputLabel id="beneficiary-label">Beneficiário</InputLabel>
              <Select
                labelId="beneficiary-label"
                name="beneficiary"
                value={formData.beneficiary}
                onChange={handleChange}
              >
                {beneficiaryTypes.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.beneficiary && <FormHelperText>{errors.beneficiary}</FormHelperText>}
            </FormControl>
          </Grid>

          {formData.discountType === DiscountType.DISTRIBUIDORA && (
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.discountValue}>
                <InputLabel id="discountValue-label">Distribuidora</InputLabel>
                <Select
                  labelId="discountValue-label"
                  name="discountValue"
                  value={formData.discountValue}
                  onChange={handleChange}
                >
                  {energyUtilities.map(option => (
                    <MenuItem key={option.id} value={option.acronym}>
                      {option.acronym}
                    </MenuItem>
                  ))}
                </Select>
                {errors.discountValue && <FormHelperText>{errors.discountValue}</FormHelperText>}
              </FormControl>
            </Grid>
          )}

          {formData.discountType === DiscountType.TIPO_PESSOA && (
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.discountValue}>
                <InputLabel id="discountValue-label">Tipo de pessoa</InputLabel>
                <Select
                  labelId="discountValue-label"
                  name="discountValue"
                  value={formData.discountValue}
                  onChange={handleChange}
                >
                  {personTypes.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.discountValue && <FormHelperText>{errors.discountValue}</FormHelperText>}
              </FormControl>
            </Grid>
          )}

          {formData.discountType === DiscountType.FAIXA_CONSUMO && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Consumo Mínimo (MW)"
                  name="mwMinValue"
                  value={formData.mwMinValue}
                  onChange={handleChange}
                  type="number"
                  error={!!errors.mwMinValue}
                  helperText={errors.mwMinValue}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Consumo Máximo (MW)"
                  name="mwMaxValue"
                  value={formData.mwMaxValue}
                  onChange={handleChange}
                  type="number"
                  error={!!errors.mwMaxValue}
                  helperText={errors.mwMaxValue}
                  placeholder="Deixe em branco para 'sem limite'"
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Descrição"
              name="description"
              value={formData.description}
              onChange={handleChange}
              error={!!errors.description}
              helperText={errors.description}
              placeholder="Ex: Bônus para clientes da distribuidora ENEL"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Valor do Desconto (R$)"
              name="discountAmount"
              value={formData.discountAmount}
              onChange={handleChange}
              type="number"
              error={!!errors.discountAmount}
              helperText={errors.discountAmount}
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Salvando...' : rule ? 'Atualizar' : 'Salvar'}
        </Button>
      </DialogActions>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        message={snackbar.message}
        onClose={() => setSnackbar({ open: false, message: '' })}
      />
    </Dialog>
  );
};

export default RegraDescontoModal;
